import type { ToolDefinition } from './types'

// A runtime registry of tools the AI can call. It mirrors the app's existing
// `eventBus` pattern: components that can service a tool register handlers when
// they mount and unregister when they leave. The agent asks the registry for
// the *currently available* tools each turn, so the AI's capabilities track
// what is on screen (e.g. `set_field` exists only while an entry editor is open).
//
// Names are stacked so nested editors (StackingView) don't clobber each other —
// the most recently registered handler for a name wins, and unregistering
// restores the previous one.
//
// Tools registered for an owner (an entry editor's entryApi) are only offered while
// that owner is the most recently registered one - a tool that exists only on the
// parent editor must not act on it while the user works in a stacked child editor.

// `signal` aborts when the user stops the chat, so long-running tools can cancel too.
type ToolContext = { signal: AbortSignal }

export type AiTool = {
	name: string,
	definition: ToolDefinition,
	handler: (input: any, context: ToolContext) => any | Promise<any>,
}

type StackEntry = AiTool & { token: number, owner?: object }

const stacks = new Map<string, StackEntry[]>()
let seq = 0

export function registerTools(tools: AiTool[], owner?: object): number {
	const token = ++seq
	for (const tool of tools) {
		const stack = stacks.get(tool.name) ?? []
		stack.push({ ...tool, token, owner })
		stacks.set(tool.name, stack)
	}
	return token
}

export function unregisterTools(token: number) {
	for (const [ name, stack ] of stacks) {
		const filtered = stack.filter(e => e.token !== token)
		if (filtered.length) stacks.set(name, filtered)
		else stacks.delete(name)
	}
}

function activeOwner(): object | undefined {
	let latest: StackEntry | undefined
	for (const stack of stacks.values()) {
		for (const entry of stack) {
			if (entry.owner && (!latest || entry.token > latest.token)) latest = entry
		}
	}
	return latest?.owner
}

function current(stack: StackEntry[] = [], owner: object | undefined): StackEntry | undefined {
	for (let i = stack.length - 1; i >= 0; i--) {
		if (!stack[i].owner || stack[i].owner === owner) return stack[i]
	}
}

export function toolDefinitions(): ToolDefinition[] {
	const owner = activeOwner()
	return [ ...stacks.values() ].flatMap(stack => current(stack, owner)?.definition ?? [])
}

export async function executeTool(name: string, input: any, signal: AbortSignal): Promise<any> {
	const tool = current(stacks.get(name), activeOwner())
	if (!tool) throw new Error('Unknown tool: ' + name)
	return tool.handler(input, { signal })
}
